import React from 'react';
import { Pencil, Mail, Clock } from 'lucide-react';
import { CampaignData } from './types';
import { sanitizeHtml } from '@/utils/sanitization';

interface EmailPreviewPanelProps {
  campaign: CampaignData;
  isChatOpen: boolean;
  onEditEmail: () => void;
}

export const EmailPreviewPanel: React.FC<EmailPreviewPanelProps> = ({
  campaign,
  isChatOpen,
  onEditEmail,
}) => {
  const formatSchedule = () => {
    const days = campaign.sendWindow.days;
    const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    const weekend = ['Sat', 'Sun'];
    const isWeekdays = weekdays.every(d => days.includes(d)) && !weekend.some(d => days.includes(d));
    const isWeekend = weekend.every(d => days.includes(d)) && !weekdays.some(d => days.includes(d));
    const isEveryDay = [...weekdays, ...weekend].every(d => days.includes(d));

    let daysLabel = '';
    if (isEveryDay) daysLabel = 'Every day';
    else if (isWeekdays) daysLabel = 'Weekdays';
    else if (isWeekend) daysLabel = 'Weekends';
    else daysLabel = days.map(d => d.charAt(0)).join(' ');

    return `${daysLabel}, ${campaign.sendWindow.start} – ${campaign.sendWindow.end}`;
  };

  return (
    <div
      className="hidden lg:flex flex-shrink-0 flex-col overflow-hidden bg-neutral-50/30 border-l border-neutral-100 transition-all duration-300"
      style={{
        width: isChatOpen ? '0px' : '380px',
        opacity: isChatOpen ? 0 : 1,
        visibility: isChatOpen ? 'hidden' : 'visible'
      }}
    >
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden shadow-sm">
          <div className="h-11 flex items-center justify-between px-4 border-b border-neutral-100 bg-neutral-50/50">
            <span className="text-[12px] font-medium text-neutral-700">Email</span>
            <button
              onClick={onEditEmail}
              className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-md transition-colors"
              title="Edit template"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-neutral-900 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-medium text-white">
                  {campaign.senderEmail.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-neutral-900">
                  {campaign.senderEmail.split('@')[0].replace(/[._]/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </p>
                <p className="text-[11px] text-neutral-400">{campaign.senderEmail}</p>
              </div>
            </div>

            <div className="border-t border-neutral-100 pt-3">
              <p className="text-[14px] font-medium text-neutral-900">{campaign.subject}</p>
            </div>

            <div className="border-t border-neutral-100 pt-3">
              <div
                className="text-[13px] text-neutral-700 leading-relaxed prose prose-sm max-w-none
                  [&_p]:mb-2 [&_p:last-child]:mb-0
                  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ul]:space-y-1
                  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_ol]:space-y-1
                  [&_li]:text-[13px] [&_li]:text-neutral-700
                  [&_a]:text-blue-600 [&_a]:underline
                  [&_strong]:font-semibold [&_em]:italic"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(campaign.body) }}
              />
            </div>
          </div>
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden shadow-sm">
          <div className="h-11 flex items-center justify-between px-4 border-b border-neutral-100 bg-neutral-50/50">
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-medium text-neutral-700">Schedule</span>
              <span className="text-[10px] font-medium text-neutral-400 bg-neutral-100 px-1.5 py-0.5 rounded">Coming soon</span>
            </div>
          </div>

          <div className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-neutral-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-neutral-400 uppercase tracking-wide mb-0.5">Sending From</p>
                <p className="text-[12px] font-medium text-neutral-900">{campaign.senderEmail}</p>
              </div>
            </div>

            <div className="border-t border-neutral-100 pt-3 opacity-50">
              <p className="text-[10px] text-neutral-400 uppercase tracking-wide mb-1">Schedule</p>
              <div className="flex items-center gap-2 text-[13px] text-neutral-700">
                <Clock className="w-3.5 h-3.5 text-neutral-400" />
                <span>{formatSchedule()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
